<template>
  <v-container v-if="charger" class="pa-5">
    <v-container class="pt-0">
      <v-card-title class="pt-0 pl-0">
        {{ charger.name }}

        <!-- conditionally rendered Network logos -->
        <v-spacer v-if="charger.operator && charger.operator.name"></v-spacer>
        <OperatorLogo :networkBranding="branding" />
      </v-card-title>
      <v-card-subtitle v-if="charger.addressString" class="pl-0">
        {{ charger.addressString }}
      </v-card-subtitle>
      <v-card-subtitle v-else>
        charging station address has not been provided
      </v-card-subtitle>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header class="primary--text">
            Charging station devices
            <template v-slot:actions>
              <v-icon color="primary" large>mdi-menu-down</v-icon>
            </template>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <EvseCard
              :evses="charger.evses"
              @selectConnector="selectConnector"
            />
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <!-- Actual charging stop info card -->
      <ChargingStopDetailsCard
        v-if="
          trip &&
          charger &&
          trip.status !== 'optimised-trip' &&
          trip.chargingStopCDBIDs.includes(charger.id)
        "
      />
      <!-- Estimated charging stop info card -->
      <ChargingCalculatorCard
        v-else-if="charger && vehicle && vehicle.evModel"
        :connector="selectedConnector"
      />
      <!-- no EV model set error display -->
      <v-card class="pa-2 mt-5 mb-5 rounded-lg" v-else>
        <v-card-text>
          Please set an EV profile for your EV to see estimates.
        </v-card-text>
      </v-card>
      <v-card flat class="pb-10">
        <v-card-title class="primary--text"> Plugs and sockets </v-card-title>
        <PlugsCard :plugs="charger.allConnectors" />
        <v-card-title class="primary--text">Photos added</v-card-title>
        <ChargingStationImageCarousel />
        <ChargingStationsAnimatedTabsCard
          :networkDisplayName="
            branding && branding.displayName ? branding.displayName : undefined
          "
          :instructions="
            branding && branding.instructions
              ? branding.instructions
              : undefined
          "
        />
      </v-card>
    </v-container>
  </v-container>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";
import PlugsCard from "./PlugsCard.vue";
import ChargingCalculatorCard from "./ChargingCalculatorCard.vue";
import ChargingStopDetailsCard from "./ChargingStopDetailsCard.vue";
import ChargingStationImageCarousel from "./ChargingStationImageCarousel.vue";
import OperatorLogo from "../common/OperatorLogo.vue";
import { GettersTypes, MutationTypes } from "@/logic/store/store_types";
import EvseCard from "./EvseCard.vue";
import Connector from "@/logic/classes/connector";
import Charger from "@/logic/classes/charger";
import Vehicle from "@/logic/classes/vehicle";
import ChargingStationsAnimatedTabsCard from "./ChargingStationsAnimatedTabsCard.vue";

/** `Vue Component:` renders expanded details of individual charging stations,
 * intended to be used with in the `SecondaryDialog` and `MobileDialog`
 * components */
export default Vue.extend({
  name: "ChargingStationDetailsContent",
  computed: {
    selectedConnector(): Connector | undefined {
      if (this.selectedConnectorId) {
        return (
          this.$store.getters[GettersTypes.selectedChargerData] as
            | Charger
            | undefined
        )?.allConnectors.find(
          (connector) => connector.id === this.selectedConnectorId
        );
      }
      return undefined;
    },
    ...mapGetters({
      charger: GettersTypes.selectedChargerData,
      branding: GettersTypes.selectedChargerBranding,
      trip: GettersTypes.selectedTripData,
      vehicle: GettersTypes.selectedVehicleData,
    }),
  },
  data() {
    return {
      value: 68,
      tab: 0,
      localRange: null,
      isBookable: false,
      selectedConnectorId: undefined as string | undefined,
    };
  },
  methods: {
    close() {
      this.$store.commit(MutationTypes.setSelectedCharger, undefined);
    },
    selectConnector(uuid: string): void {
      this.selectedConnectorId = uuid;
    },
    setInitialValues(): void {
      const vehicle: Vehicle | undefined = this.vehicle;
      const charger: Charger | undefined = this.charger;
      if (vehicle && charger) {
        const connectors = charger.allConnectors.filter(
          (connector) => connector.isCompatible(vehicle) !== "incompatible"
        );
        if (connectors.length) this.selectConnector(connectors[0].id);
      } else if (charger) {
        this.selectConnector(charger.evses[0].connectors[0].id);
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setInitialValues();
    });
  },
  components: {
    OperatorLogo,
    PlugsCard,
    ChargingCalculatorCard,
    ChargingStopDetailsCard,
    ChargingStationImageCarousel,
    EvseCard,
    ChargingStationsAnimatedTabsCard,
  },
});
</script>

<style scoped>
#pwt-custom-active-tab {
  border-radius: 10px 10px 0 0;
  background-color: var(--v-primary-base);
}

.pwt-tabs-border {
  border-bottom: 2px solid var(--v-primary-base);
}

.pwt-custom-active-tab-text {
  z-index: 1500;
}
* >>> .v-slider--horizontal .v-slider__track-container {
  height: 6px; /* override default slider thickness */
}

* >>> .v-slider__track-fill {
  border-radius: 2px; /* override default slider border-radius */
}

* >>> .v-slider__track-background {
  border-radius: 2px; /* override default slider border-radius */
}
</style>
